"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const H = ["a3f9c1", "7b2e44", "c81d05", "e4920a", "f15b3c", "9d8a20"];
const FEAT_KEYS = ["Git_branch_c_feat1", "Git_branch_c_feat2", "Git_branch_c_feat3"];
const MAX_FEAT = 3;

const ROW_H = 50;
const TOP = 26;
const X_MAIN = 26;
const X_FEAT = 74;
const R = 7;
const y = (row) => TOP + row * ROW_H;

export default function GitBranches() {
    const t = useTranslations();

    // Noms de branches (main, feature) rendus en italique dans le texte.
    const branchName = { m: (chunks) => <em className="italic">{chunks}</em> };

    const [branched, setBranched] = useState(false);
    const [featureCommits, setFeatureCommits] = useState(0);
    const [merged, setMerged] = useState(false);

    function reset() {
        setBranched(false);
        setFeatureCommits(0);
        setMerged(false);
    }

    // Construction des lignes du graphe à partir de l'état.
    const rows = [
        { lane: "main", msgKey: "Git_branch_c_main1", hash: H[0], kind: "commit" },
        { lane: "main", msgKey: "Git_branch_c_main2", hash: H[1], kind: "commit" },
    ];
    const branchRow = 1;
    let firstFeatureRow = null;
    let lastFeatureRow = null;
    if (branched) {
        if (featureCommits === 0) {
            rows.push({ lane: "feature", msgKey: "Git_branch_empty", hash: "", kind: "ghost" });
            firstFeatureRow = rows.length - 1;
            lastFeatureRow = rows.length - 1;
        } else {
            for (let i = 0; i < featureCommits; i++) {
                rows.push({ lane: "feature", msgKey: FEAT_KEYS[i], hash: H[2 + i], kind: "commit" });
                if (i === 0) firstFeatureRow = rows.length - 1;
                lastFeatureRow = rows.length - 1;
            }
        }
    }
    let mergeRow = null;
    if (merged) {
        rows.push({ lane: "main", msgKey: "Git_branch_c_merge", hash: H[5], kind: "merge" });
        mergeRow = rows.length - 1;
    }

    const lastMainRow = merged ? mergeRow : branchRow;
    const height = y(rows.length - 1) + 28;

    const phase = merged
        ? "merged"
        : featureCommits > 0
          ? "committed"
          : branched
            ? "branched"
            : "initial";

    return (
        <section id="git-branches" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Git_branch_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t.rich("Git_branch_intro", branchName)}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-violet-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-fuchsia-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:git-icon"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        git graph
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Git_branch_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-6 items-start">
                    {/* Graphe */}
                    <div className="min-w-0 rounded-2xl bg-black/30 ring-1 ring-white/10 p-3 overflow-x-auto">
                        <div className="flex items-center gap-4 px-2 pt-1 pb-3 text-[11px] font-mono">
                            <span className="inline-flex items-center gap-1.5 text-violet-300">
                                <span className="w-2.5 h-2.5 rounded-full bg-violet-400" />
                                {t.rich("Git_branch_legend_main", branchName)}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-emerald-300">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                                {t.rich("Git_branch_legend_feature", branchName)}
                            </span>
                        </div>
                        <svg
                            viewBox={`0 0 380 ${height}`}
                            width="100%"
                            style={{ maxWidth: 430 }}
                            role="img"
                        >
                            {/* Ligne main */}
                            <line
                                x1={X_MAIN}
                                y1={y(0)}
                                x2={X_MAIN}
                                y2={y(lastMainRow)}
                                stroke="#8b5cf6"
                                strokeOpacity="0.55"
                                strokeWidth="2.5"
                            />
                            {/* Ligne feature */}
                            {firstFeatureRow !== null &&
                                lastFeatureRow > firstFeatureRow && (
                                    <line
                                        x1={X_FEAT}
                                        y1={y(firstFeatureRow)}
                                        x2={X_FEAT}
                                        y2={y(lastFeatureRow)}
                                        stroke="#10b981"
                                        strokeOpacity="0.6"
                                        strokeWidth="2.5"
                                    />
                                )}
                            {/* Courbe de branche */}
                            {branched && firstFeatureRow !== null && (
                                <path
                                    d={`M ${X_MAIN} ${y(branchRow)} C ${X_MAIN} ${y(branchRow) + 25}, ${X_FEAT} ${y(firstFeatureRow) - 25}, ${X_FEAT} ${y(firstFeatureRow)}`}
                                    fill="none"
                                    stroke="#10b981"
                                    strokeOpacity="0.6"
                                    strokeWidth="2.5"
                                    strokeDasharray={
                                        featureCommits === 0 ? "4 4" : "0"
                                    }
                                />
                            )}
                            {/* Courbe de merge */}
                            {merged && lastFeatureRow !== null && (
                                <path
                                    d={`M ${X_FEAT} ${y(lastFeatureRow)} C ${X_FEAT} ${y(lastFeatureRow) + 25}, ${X_MAIN} ${y(mergeRow) - 25}, ${X_MAIN} ${y(mergeRow)}`}
                                    fill="none"
                                    stroke="#8b5cf6"
                                    strokeOpacity="0.6"
                                    strokeWidth="2.5"
                                />
                            )}
                            {/* Noeuds + libellés */}
                            {rows.map((r, i) => {
                                const cx = r.lane === "main" ? X_MAIN : X_FEAT;
                                const cy = y(i);
                                return (
                                    <g key={i}>
                                        {r.kind === "ghost" ? (
                                            <circle
                                                cx={cx}
                                                cy={cy}
                                                r={R}
                                                fill="#0b1220"
                                                stroke="#10b981"
                                                strokeWidth="2"
                                                strokeDasharray="3 3"
                                            />
                                        ) : (
                                            <circle
                                                cx={cx}
                                                cy={cy}
                                                r={R}
                                                fill={
                                                    r.lane === "main"
                                                        ? "#a78bfa"
                                                        : "#34d399"
                                                }
                                            />
                                        )}
                                        {r.kind === "merge" && (
                                            <circle
                                                cx={cx}
                                                cy={cy}
                                                r={3}
                                                fill="#fff"
                                            />
                                        )}
                                        <text
                                            x="98"
                                            y={cy + 4}
                                            fontSize="12.5"
                                            fontFamily="ui-monospace, monospace"
                                        >
                                            {r.hash ? (
                                                <tspan fill="#6b7280">
                                                    {r.hash}{"  "}
                                                </tspan>
                                            ) : null}
                                            <tspan
                                                fill={
                                                    r.kind === "ghost"
                                                        ? "#9ca3af"
                                                        : "#e5e7eb"
                                                }
                                            >
                                                {t(r.msgKey)}
                                            </tspan>
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>
                    </div>

                    {/* Contrôles + statut */}
                    <div className="min-w-0 flex flex-col gap-4">
                        <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-3 text-sm text-gray-300 leading-relaxed">
                            {t.rich(`Git_branch_status_${phase}`, branchName)}
                        </div>

                        <div className="flex flex-col gap-2.5">
                            {!branched && (
                                <button
                                    type="button"
                                    onClick={() => setBranched(true)}
                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 px-4 py-2.5 text-sm font-semibold text-white transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="branching-paths-down-bold-duotone"
                                        className="w-4 h-4"
                                    />
                                    {t("Git_branch_btn_branch")}
                                </button>
                            )}
                            {branched && !merged && featureCommits < MAX_FEAT && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFeatureCommits((c) => c + 1)
                                    }
                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-emerald-400/30 px-4 py-2.5 text-sm font-semibold text-emerald-200 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="code-square-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Git_branch_btn_commit")}
                                </button>
                            )}
                            {branched && featureCommits >= 1 && !merged && (
                                <button
                                    type="button"
                                    onClick={() => setMerged(true)}
                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-500 hover:bg-violet-400 px-4 py-2.5 text-sm font-semibold text-white transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="arrow-right-down-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Git_branch_btn_merge")}
                                </button>
                            )}
                            {(branched || merged) && (
                                <button
                                    type="button"
                                    onClick={reset}
                                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 text-xs text-gray-400 transition active:scale-95 self-start"
                                >
                                    <SolarIcon
                                        name="restart-bold-duotone"
                                        className="w-3.5 h-3.5"
                                    />
                                    {t("Git_branch_btn_reset")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Git_branch_caption")}
            </p>
        </section>
    );
}
